import { GenericColDef, AlignDirection, SortByFilter, GridContext, CellTemplateType } from './grid-context';
import { DrEntity } from './dr-entity';
import { isArray } from './utils';
import { GenericTreeNode } from '../components/generic-tree/generic-tree-node';
import { EntityType } from './enums';
import * as _ from 'underscore';
import { ViewType } from './view';
import { Corpus } from './corpus';
import { ValueGetterParams } from 'ag-grid-community';

export enum ReportType {
    BILLING_REPORT = 'BILLING_REPORT',
    CORPUS_SOURCE = 'CORPUS_SOURCE',
    CONSOLIDATED_BILLING_REPORT = 'CONSOLIDATED_BILLING_REPORT',
    CUSTODIAN_DIRECTORY_REPORT = 'CUSTODIAN_DIRECTORY_REPORT',
    CLUSTER = 'CLUSTER',
    CUSTOM_WARNINGS_ERRORS = 'CUSTOM_WARNINGS_ERRORS',
    DATA_PROCESSING_REPORT = 'DATA_PROCESSING_REPORT',
    SOURCE_TRACKING_REPORT = 'SOURCE_TRACKING_REPORT',
    DATE_EMAIL_RECEIVED = 'DATE_EMAIL_RECEIVED',
    DATE_EMAIL_SENT = 'DATE_EMAIL_SENT',
    DATE_PRIMARY = 'DATE_PRIMARY',
    DOCUMENT_CLASS = 'DOCUMENT_CLASS',
    DOMINANT_LANGUAGE = 'DOMINANT_LANGUAGE',
    EMAIL_RECEIVED_DOMAINS = 'EMAIL_RECEIVED_DOMAINS',
    EMAIL_RECEIVER = 'EMAIL_RECEIVER',
    EMAIL_SENDER = 'EMAIL_SENDER',
    EMAIL_SENT_DOMAINS = 'EMAIL_SENT_DOMAINS',
    EXPORT_COMPARE = 'EXPORT_COMPARE',
    EXPORT_COMPARE_STATISTICS = 'EXPORT_COMPARE_STATISTICS',
    EXPORT_LOAD_FILE_REPORT = 'EXPORT_LOAD_FILE_REPORT',
    EXPORT_PRODUCE_REPORT = 'EXPORT_PRODUCE_REPORT',
    EXPORT_PREPARE_REPORT = 'EXPORT_PREPARE_REPORT',
    FILETYPE = 'FILETYPE',
    GROUP_VIEW_MEMBERSHIP = 'GROUP_VIEW_MEMBERSHIP',
    IMPORT_ERROR = 'IMPORT_ERROR',
    IMPORT = 'IMPORT',
    LANGUAGE = 'LANGUAGE',
    MAG_HIT_COUNT = 'MAG_HIT_COUNT',
    METADATA_BULK_SEARCH_SUMMARY = 'METADATA_BULK_SEARCH_SUMMARY',
    OCR_CANDIDATES = 'OCR_CANDIDATES',
    OCR_CONFIDENCE_LEVEL = 'OCR_CONFIDENCE_LEVEL',
    OCR_SOURCE = 'OCR_SOURCE',
    REVIEW_FOLDER = 'REVIEW_FOLDER',
    SEARCH = 'SEARCH',
    TAG_SUMMARY = 'TAG_SUMMARY',
    TECHNICAL_REDUCTION = 'TECHNICAL_REDUCTION',
    TECHNICAL_REDUCTION_VERTICAL = 'TECHNICAL_REDUCTION_VERTICAL',
    TERM_SEARCH_HIT_COUNT = 'TERM_SEARCH_HIT_COUNT',
    TERM_SEARCH_HIT_COUNT_VERTICAL = 'TERM_SEARCH_HIT_COUNT_VERTICAL',
    WORKFLOW = 'WORKFLOW',
    DOMAIN_COMMUNICATION = 'DOMAIN_COMMUNICATION',
    EMAIL_COMMUNICATION = 'EMAIL_COMMUNICATION',
    AUXPARSINGSTATUS = 'AUXPARSINGSTATUS',
    SLACK_DATA_REPORT = 'SLACK_DATA_REPORT'
}

export enum ReportDisplayType {
    NONE = 'NONE',
    GRID = 'GRID',
    PIE_CHART = 'PIE_CHART',
    BAR_CHART = 'BAR_CHART'
}

export enum ReportSearchType {
    FREEFORM = 'Freeform',
    ADVANCED = 'Advanced',
    QUERY = 'Query',
    DRILL_THROUGH = 'Drill-through',
    MORE_LIKE_THESE = 'More Like These',
    MORE_LIKE_THIS = 'More Like This',
    NEAR_DUPLICATE_DOCUMENT = 'Near Duplicate Document',
    EXACT_DUPLICATE_GROUP = 'Exact Duplicate Group',
    CONTENT_DUPLICATE_GROUP = 'Content Duplicate Group',
    EXACT_DUPLICATE_DOCUMENT = 'Exact Duplicate Document',
    CONTENT_DUPLICATE_DOCUMENT = 'Content Duplicate Document',
    BY_PROFILE = 'By Profile',
    COMBINED = 'Combined',
    SAMPLE_BY_SIZE = 'Sample by Size',
    SAMPLE_BY_CONFIDENCE_LEVEL = 'Sample by Confidence Level',
    BULK = 'BULK',
    BY_SYNTHETIC_DOCUMENT = 'By Synthetic Document'
}

export enum AliasReportTitle {
    BILLING_REPORT = 'Billing Summary',
    GROUP_VIEW_MEMBERSHIP = 'Custodians',
    CUSTODIAN_DIRECTORY_REPORT = 'Custodian Directories',
    CUSTOM_WARNINGS_ERRORS = 'Custom Warnings and Errors',
    DATE_PRIMARY = 'Date Report',
    DOCUMENT_CLASS = 'Document Classes',
    TECHNICAL_REDUCTION = 'Document Classification',
    FILETYPE = 'Document Types',
    DOMINANT_LANGUAGE = 'Dominant Languages',
    EMAIL_RECEIVER = 'Email Addresses Received',
    EMAIL_SENDER = 'Email Addresses Sent',
    DATE_EMAIL_RECEIVED = 'Email Received Date',
    DATE_EMAIL_SENT = 'Email Sent Date',
    LANGUAGE = 'Languages',
    OCR_CANDIDATES = 'OCR Candidates',
    OCR_CONFIDENCE_LEVEL = 'OCR Confidence',
    OCR_SOURCE = 'OCR Sources',
    EMAIL_RECEIVED_DOMAINS = 'Receiving Domains',
    IMPORT = 'Scan Summary',
    EMAIL_SENT_DOMAINS = 'Sending Domains',
    CORPUS_SOURCE = 'Sources',
    TAG_SUMMARY = 'Tags',
    IMPORT_ERROR = 'Warnings and Errors',
    REVIEW_FOLDER = 'Folder Paths'
}

enum ReportColumnType {
    name,
    countPercent,
    sizePercent,
    doccount,
    blank,
    totalsize,
    checkbox,
    searchTask,
    results,
    documentClass,
    count,
    size,
    dupCount,
    dupSize,
    nameLeft,
    nameLeftReadonlyInput,
    contextMenu,
    notYetProcessed,
    successfulProcessed,
    failedProcessed,
    totalSizeExport,
    totalSizeExportStats,
    totalSizeGB,
    itemExport,
    countPercentExport,
    sizePercentExport,
    documentTypes,
    countLabelColor,
    nameLeftNoSort,
    countNoSort,
    sizeNoSort,
    itemExportNoSort
}

const reportColumns: ReadonlyMap<ReportColumnType, GenericColDef> = new Map<ReportColumnType, GenericColDef>([
    [ReportColumnType.blank, {
        headerName: '',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left
    }],
    [ReportColumnType.name, {
        headerName: 'Name',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: SortByFilter.NAME.toLowerCase()
    }],
    [ReportColumnType.countPercent, {
        headerName: '% Count',
        field: 'countPercent',
        automationId: '',
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right
    }],
    [ReportColumnType.sizePercent, {
        headerName: '% Size',
        field: 'sizePercent',
        automationId: '',
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right
    }],
    [ReportColumnType.totalsize, {
        headerName: 'Total File Size (GB)',
        field: 'Size',
        automationId: '',
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right
    }],
    [ReportColumnType.doccount, {
        headerName: 'Document Count',
        automationId: '',
        field: 'Count',
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right
    }],
    [ReportColumnType.checkbox, {
        colId: 'checkbox', automationId: '',
        checkboxSelection: true, headerCheckboxSelection: true,
        minWidth: 36, maxWidth: 36, width: 36,
        suppressSizeToFit: true, suppressMovable: true
    }],
    [ReportColumnType.searchTask, {
        headerName: 'Search Task',
        lockVisible: true,
        automationId: '',
        field: 'name',
        valueGetter: (params: ValueGetterParams) => (params && params.data && params.data.name) ? params.data.name.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '',
    }],
    [ReportColumnType.results, {
        headerName: 'Results',
        automationId: '',
        field: 'data.documentCount',
        width: 100,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right
    }],
    [ReportColumnType.documentClass, {
        headerName: 'Document Class',
        lockVisible: true,
        automationId: '',
        field: 'documentClass',
        sortBy: 'documentClass'
    }],
    [ReportColumnType.count, {
        headerName: 'Count',
        field: 'count',
        automationId: '',
        width: 100,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: SortByFilter.COUNT.toLowerCase()
    }],
    [ReportColumnType.size, {
        headerName: 'Size',
        field: 'size',
        automationId: '',
        width: 110,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'unformattedSize'
    }],
    [ReportColumnType.itemExport, {
        headerName: 'Item',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
        sortBy: SortByFilter.NAME.toLowerCase()
    }],
    [ReportColumnType.totalSizeExport, {
        headerName: 'Total Size (GB)',
        field: 'totalSize',
        automationId: '',
        width: 120,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
    }],
    [ReportColumnType.totalSizeExportStats, {
        headerName: 'Total Size (GB)',
        field: 'size',
        automationId: '',
        width: 120,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'size'
    }],
    [ReportColumnType.totalSizeGB, {
        headerName: 'Total Size',
        field: 'size',
        automationId: '',
        width: 120,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: SortByFilter.SIZE.toLowerCase()
    }],
    [ReportColumnType.countPercentExport, {
        headerName: '% Count',
        field: 'countPercent',
        automationId: '',
        width: 120,
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
    }],
    [ReportColumnType.sizePercentExport, {
        headerName: '% Size',
        field: 'sizePercent',
        automationId: '',
        width: 120,
        cellTemplateType: CellTemplateType.boldLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
    }],
    [ReportColumnType.dupCount, {
        headerName: 'Deduplicated Count',
        field: 'dupCount',
        automationId: '',
        width: 200,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'dupCount'
    }],
    [ReportColumnType.dupSize, {
        headerName: 'Deduplicated Size',
        field: 'dupSize',
        automationId: '',
        width: 200,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'unformattedDupSize'
    }],
    [ReportColumnType.nameLeft, {
        headerName: 'Name',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
        sortBy: SortByFilter.NAME.toLowerCase()
    }],
    [ReportColumnType.nameLeftReadonlyInput, {
        headerName: 'Name',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.readonlyInputLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
        sortBy: SortByFilter.NAME.toLowerCase()
    }],
    [ReportColumnType.contextMenu, {
        automationId: '',
        cellTemplateType: CellTemplateType.detailsReport,
        minWidth: 30, maxWidth: 30, width: 30,
        suppressSizeToFit: true
    }],
    [ReportColumnType.notYetProcessed, {
        headerName: 'Not Yet Processed',
        field: 'notYet',
        automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'notYet'
    }],
    [ReportColumnType.successfulProcessed, {
        headerName: 'Successfully Processed',
        field: 'success',
        automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'success'
    }],
    [ReportColumnType.failedProcessed, {
        headerName: 'Failed Processing',
        field: 'failed',
        automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        sortBy: 'failed'
    }],
    [ReportColumnType.documentTypes, {
        headerName: 'Document Types',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
    }],
    [ReportColumnType.countLabelColor, {
        headerName: 'Count',
        field: 'count',
        automationId: '',
        width: 100,
        cellTemplateType: CellTemplateType.redLabel,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
    }],
    [ReportColumnType.nameLeftNoSort, {
        headerName: 'Name',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
    }],
    [ReportColumnType.countNoSort, {
        headerName: 'Count',
        field: 'count',
        automationId: '',
        width: 100,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
    }],
    [ReportColumnType.sizeNoSort, {
        headerName: 'Size',
        field: 'size',
        automationId: '',
        width: 110,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right
    }],
    [ReportColumnType.itemExportNoSort, {
        headerName: 'Item',
        field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left
    }],
]);

export const ReportDefinitions: ReadonlyMap<ReportType, Array<GenericColDef>> = new Map<ReportType, Array<GenericColDef>>([
    [ReportType.BILLING_REPORT, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.documentClass),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size)
    )],
    [ReportType.WORKFLOW, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.blank),
        reportColumns.get(ReportColumnType.doccount),
        reportColumns.get(ReportColumnType.countPercent),
        reportColumns.get(ReportColumnType.totalsize),
        reportColumns.get(ReportColumnType.sizePercent)

    )],
    [ReportType.SEARCH, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.checkbox),
        reportColumns.get(ReportColumnType.searchTask),
        reportColumns.get(ReportColumnType.results)
    )],
    [ReportType.TECHNICAL_REDUCTION, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size),
        reportColumns.get(ReportColumnType.dupCount),
        reportColumns.get(ReportColumnType.dupSize)
    )],
    [ReportType.TECHNICAL_REDUCTION_VERTICAL, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size),
        reportColumns.get(ReportColumnType.dupCount),
        reportColumns.get(ReportColumnType.dupSize)
    )],
    [ReportType.CUSTOM_WARNINGS_ERRORS, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size),
        reportColumns.get(ReportColumnType.contextMenu),
    )],
    [ReportType.IMPORT_ERROR, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size),
        reportColumns.get(ReportColumnType.contextMenu),
    )],
    [ReportType.OCR_CONFIDENCE_LEVEL, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size),
    )],
    [ReportType.OCR_CANDIDATES, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.size),
    )],
    [ReportType.OCR_SOURCE, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeft),
        reportColumns.get(ReportColumnType.notYetProcessed),
        reportColumns.get(ReportColumnType.successfulProcessed),
        reportColumns.get(ReportColumnType.failedProcessed),
    )],
    [ReportType.IMPORT, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeftNoSort),
        reportColumns.get(ReportColumnType.countNoSort),
        reportColumns.get(ReportColumnType.sizeNoSort),
    )],
    [ReportType.EXPORT_COMPARE_STATISTICS, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.itemExport),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.totalSizeExportStats),
    )],
    [ReportType.EXPORT_COMPARE, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.itemExportNoSort),
        reportColumns.get(ReportColumnType.countNoSort),
        reportColumns.get(ReportColumnType.totalSizeExport),
        reportColumns.get(ReportColumnType.countPercentExport),
        reportColumns.get(ReportColumnType.sizePercentExport),
    )],
    [ReportType.EXPORT_LOAD_FILE_REPORT, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.documentTypes),
        reportColumns.get(ReportColumnType.countLabelColor),
    )],
    [ReportType.EXPORT_PREPARE_REPORT, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.itemExportNoSort),
        reportColumns.get(ReportColumnType.countLabelColor),
    )],
    [ReportType.EXPORT_PRODUCE_REPORT, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.itemExportNoSort),
        reportColumns.get(ReportColumnType.countLabelColor),
    )],
    [ReportType.REVIEW_FOLDER, new Array<GenericColDef>(
        reportColumns.get(ReportColumnType.nameLeftReadonlyInput),
        reportColumns.get(ReportColumnType.count),
        reportColumns.get(ReportColumnType.totalSizeGB),
    )],
]);

export class Report extends DrEntity {

    dateUpdated: string;
    title: string;
    reportEntryList: any[];
    totalCount: number;
    reportType: string;
    reportData: string[];
    reportEntries: Array<any>;
    formatedData = [];
    clauseColumnName: string;

    constructor(value?: Partial<Report>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }
        if (value && value.reportEntryList && value.reportEntryList.length > 0) {
            this.formatedData = value.reportEntryList;
        }
    }
}

export class SearchReport extends Report {
    // attributes
    targetSize: string;
    hits = '0';
    targetName: string;
    targetHandle: string;
    searchType: string;
    queryEntered: string;
    queryExecuted: string;
    synonymExpansion: boolean;
    threshold: string;
    includeFamilies: boolean;
    includeMetadata: boolean;
    sources: string;
    families: string;
    uniqueReport: string;
    threads: string;
    filenameEntered: string;
    confidenceLevel = ' ';
    marginOfError = ' ';
    sampleAllCustodians = false;
    resultsSampleSize = ' ';
    percentOfDocuments = ' ';
    isBulkSearchNode = false;
    runDate: string;
    inputFile: string;
    connectorName: string;
    bulkSearchChunkSize: string;
    isWorkflowStepSearchResult = false;

    constructor(value?: Partial<Report>) {
        super(value);
        if (value) {
            if (value.attributes) {
                value.attributes.forEach(attribute => {
                    // attributes
                    if (attribute.name === 'targetsize') { this.targetSize = attribute.value; }
                    if (attribute.name === 'hits') {
                        this.hits = attribute.value;
                        this.resultsSampleSize = attribute.value;
                    }
                    if (attribute.name === 'targetname') {
                        this.targetName = attribute.value;
                        if (this.targetName === 'Case Data' || this.targetName.trim() === 'CaseData') {
                            this.targetName = 'Project Data';
                        }
                    }
                    if (attribute.name === 'searchtype') {
                        switch (attribute.value.toLowerCase()) {
                            case 'profile':
                                this.searchType = ReportSearchType.MORE_LIKE_THESE;
                                break;
                            case 'document':
                                this.searchType = ReportSearchType.MORE_LIKE_THIS;
                                break;
                            case 'term':
                                this.searchType = ReportSearchType.QUERY;
                                break;
                            case 'exact_duplicate_document':
                                this.searchType = ReportSearchType.EXACT_DUPLICATE_DOCUMENT;
                                break;
                            case 'content_duplicate_document':
                                this.searchType = ReportSearchType.CONTENT_DUPLICATE_DOCUMENT;
                                break;
                            case 'exact_duplicate_group':
                                this.searchType = ReportSearchType.EXACT_DUPLICATE_GROUP;
                                break;
                            case 'content_duplicate_group':
                                this.searchType = ReportSearchType.CONTENT_DUPLICATE_GROUP;
                                break;
                            case 'near_duplicate_document':
                                this.searchType = ReportSearchType.NEAR_DUPLICATE_DOCUMENT;
                                break;
                            default:
                                this.searchType = attribute.value;
                                break;
                        }
                    }
                    if (attribute.name === 'targethandle') { this.targetHandle = attribute.value; }
                    if (attribute.name === 'queryEntered') { this.queryEntered = attribute.value; }
                    if (attribute.name === 'queryExecuted') { this.queryExecuted = attribute.value; }
                    if (attribute.name === 'synonymExpansion') { this.synonymExpansion = attribute.value === 'true' ? true : false; }
                    if (attribute.name === 'threshold') { this.threshold = attribute.value + ' %'; }
                    if (attribute.name === 'familyExpansion' || attribute.name === 'includeFamily') { this.includeFamilies = attribute.value === 'true' ? true : false; }
                    if (attribute.name === 'metadataExpansion') { this.includeMetadata = attribute.value === 'true' ? true : false; }
                    if (attribute.name === 'filterName') { this.sources = attribute.value.replace(/,/g, ';'); }
                    if (attribute.name === 'familyhits') { this.families = attribute.value; }
                    if (attribute.name === 'threads') { this.threads = attribute.value; }
                    if (attribute.name === 'uniqueReport') { this.uniqueReport = attribute.value; }
                    if (attribute.name === 'sourcedocname') { this.filenameEntered = attribute.value; }
                    if (attribute.name === 'confidenceLevel') { this.confidenceLevel = attribute.value + ' %'; }
                    if (attribute.name === 'marginError') { this.marginOfError = attribute.value; }
                    if (attribute.name === 'sampleAllCustodians') { this.sampleAllCustodians = attribute.value === 'true' ? true : false; }
                    if (attribute.name === 'percentDocs') { this.percentOfDocuments = attribute.value; }
                    if (attribute.name === 'sampleType') {
                        if (attribute.value.toLowerCase() === 'sample by size') {
                            this.searchType = ReportSearchType.SAMPLE_BY_SIZE;
                        } else {
                            this.searchType = ReportSearchType.SAMPLE_BY_CONFIDENCE_LEVEL;
                        }
                    }
                    if (attribute.name === 'searchbyprofile') {
                        this.searchType = ReportSearchType.BY_PROFILE;
                    }
                    if (attribute.name === 'queryExecutedOn') { this.runDate = attribute.value; }
                    if (attribute.name === 'filename') { this.inputFile = attribute.value; }
                    if (attribute.name === 'connector') { this.connectorName = attribute.value; }
                    if (attribute.name === 'bulkSearchChunkSize') { this.bulkSearchChunkSize = attribute.value; }
                });
            }
        }
    }
}

export class SearchResultReport extends Report {
    pinnedRow: any;

    constructor(value?: Partial<Report>) {
        super(value);
        this.formatedData = [];
        if (value && value.reportData && value.reportData.length > 0) {
            let colNames = value.reportData[0].split(',');
            colNames = colNames.map(f => f.replace(/ /g, ''));
            for (let i = 1; i < value.reportData.length; i++) {
                const data = value.reportData[i].split('\u007F');
                const searchEntry = new Object;
                for (let index = 0; index < data.length; index++) {
                    searchEntry[colNames[index]] = data[index].replace(/""/g, '"') + (colNames[index] && colNames[index].indexOf('Filesize') !== -1 ? ' GB' : '');
                }
                if (i === value.reportData.length - 1) {
                    this.pinnedRow = searchEntry;
                } else {
                    this.formatedData.push(searchEntry);
                }
            }
        }
    }
}

export class ExportComparisonReport extends Report {

    constructor(value?: Partial<Report>) {
        super(value);
        this.formatedData = [];
        if (value && value.reportData && value.reportData.length > 0) {
            for (let i = 1; i < value.reportData.length; i++) {
                const data = value.reportData[i].split('\u007F');
                const entry = {
                    name: data[0],
                    count: this.trimData(data[1]),
                    totalSize: this.trimData(data[2]),
                    countPercent: this.trimData(data[3]),
                    sizePercent: this.trimData(data[4]),
                };
                this.formatedData.push(entry);
            }
        }
    }

    trimData(data: string): string {
        return data.replace(/[()]/g, '');
    }
}

export class WorkflowReport extends Report {

    lastExecutedOn: any;
    // attributes
    lastExecutionHandle: string;
    searchReportHandle: string;
    targetname: string;
    targetcount: string;
    culledcount: string;
    resultcount: string;
    updatedDocumentCount: string;
    documentCount: string;
    noOfResults: string;
    workflowname: string;
    foldername: string;
    lastsearchhandle: string;
    searchreporthandle: string;
    comparereporthandle: string;
    comparestreamhandle: string;
    lastexecution: string;
    stepquery: string;
    steptype: string;
    stepdescription: string;
    xlsx: boolean;
    queryItems: any;
    lastRanQuery: string;
    dataSetItem: any;
    folderItem: any;
    reportEntry: any;
    isRunning: boolean;

    constructor(value?: Partial<WorkflowReport>) {
        super(value);
        if (value) {
            if (value.attributes) {
                value.attributes.forEach(attribute => {
                    if (attribute.name === 'lastExecutionHandle') { this.lastExecutionHandle = attribute.value; }
                    if (attribute.name === 'isRunning') { this.isRunning = JSON.parse(attribute.value); }
                    if (attribute.name === 'searchReportHandle') { this.searchReportHandle = attribute.value; }
                    if (attribute.name === 'targetname') {
                        this.targetname = attribute.value;
                        if (this.targetname === 'Case Data' || this.targetname.trim() === 'CaseData') {
                            this.targetname = 'Project Data';
                        }
                    }
                    if (attribute.name === 'targetcount') { this.targetcount = attribute.value; }
                    if (attribute.name === 'culledcount') { this.culledcount = attribute.value; }
                    if (attribute.name === 'resultcount') { this.resultcount = attribute.value; }
                    if (attribute.name === 'updatedDocumentCount') { this.updatedDocumentCount = attribute.value; }
                    if (attribute.name === 'documentCount') { this.documentCount = attribute.value; }
                    if (attribute.name === 'noOfResults') { this.noOfResults = attribute.value; }
                    if (attribute.name === 'workflowname') { this.workflowname = attribute.value; }
                    if (attribute.name === 'lastsearchhandle') { this.lastsearchhandle = attribute.value; }
                    if (attribute.name === 'searchreporthandle') { this.searchreporthandle = attribute.value; }
                    if (attribute.name === 'comparereporthandle') { this.comparereporthandle = attribute.value; }
                    if (attribute.name === 'comparestreamhandle') { this.noOfResults = attribute.value; }
                    if (attribute.name === 'xlsx') { this.xlsx = JSON.parse(attribute.value); }
                });
            }

            value.lastExecutedOn = new Date(value.lastExecutedOn);

            if (value.queryItems) {
                this.queryItems = [];
            }

            if (value.dataSetItem) {
                value.dataSetItem.attributes.forEach(attribute => {
                    if (attribute.name === 'updatedDocumentCount') { this.dataSetItem.updatedDocumentCount = attribute.value; }
                    if (attribute.name === 'documentCount') { this.dataSetItem.documentCount = attribute.value; }
                });
            }

            if (value.folderItem && value.folderItem.attributes) {
                value.folderItem.attributes.forEach(attribute => {
                    if (attribute.name === 'noOfResults') { this.folderItem.documentCount = attribute.value; }
                });
            }

            if (value.lastRanQuery) {
                this.lastRanQuery = null;
            }

            if (value.queryItems && isArray(value.queryItems.items)) {
                this.queryItems = value.queryItems.items;
            }

            if (value.reportData && isArray(value.reportData)) {
                this.reportEntries = [];
                let item = { query: { stepquery: '', steptype: '', stepdescription: '', stepqueryDisplayed: '' }, data: [] };
                value.reportData.forEach((entry, index) => {
                    const data = entry.split('\u007F');
                    if (data.length === 5) {
                        if (data[0] !== '') {
                            // normal item data => add row
                            item.data.push({
                                name: data[0],
                                Count: data[1],
                                Size: data[2],
                                countPercent: data[3],
                                sizePercent: data[4],
                            });
                        }
                        if (index === value.reportData.length - 1 || data[0] === '') {
                            // end of entries or step separator => create reportEntry

                            item.data[item.data.length - 1].isBold = true;

                            if (value.attributes) {
                                value.attributes.forEach(attribute => {
                                    // attributes
                                    if (attribute.name === 'stepquery' + (this.reportEntries.length + 1)) {
                                        item.query.stepqueryDisplayed = attribute.value.length === 255 ? attribute.value + '\ ...' : attribute.value;
                                        item.query.stepquery = attribute.value + ' in ' + this.targetname;
                                    }
                                    if (attribute.name === 'steptype' + (this.reportEntries.length + 1)) { item.query.steptype = attribute.value; }
                                    if (attribute.name === 'stepdescription' + (this.reportEntries.length + 1)) { item.query.stepdescription = attribute.value; }
                                });
                            }
                            this.reportEntries.push(item);
                            item = { query: { stepquery: '', steptype: '', stepdescription: '', stepqueryDisplayed: '' }, data: [] };
                        }
                    }
                });

            }
        }
    }
}

export class MetadataBulkSearchReport extends Report {
    // attributes
    inputFile: string;
    includeFamilies: boolean;
    searchType: string;
    targetSize: string;
    targetName: string;
    targetHandle: string;
    hits = '0';
    metadataField: string;
    sources: string;
    providedValues = '0';
    multipleMatchedValues = '0';
    matchedValues = '0';
    missedValues = '0';

    constructor(value?: Partial<Report>) {
        super(value);
        if (value) {
            if (value.attributes) {
                value.attributes.forEach(attribute => {
                    // attributes
                    if (attribute.name === 'targetsize') { this.targetSize = attribute.value; }
                    if (attribute.name === 'hits') {
                        this.hits = attribute.value;
                    }
                    if (attribute.name === 'targetname') {
                        this.targetName = attribute.value;
                        if (this.targetName === 'Case Data' || this.targetName.trim() === 'CaseData') {
                            this.targetName = 'Project Data';
                        }
                    }
                    if (attribute.name === 'searchtype') { this.searchType = attribute.value; }
                    if (attribute.name === 'targethandle') { this.targetHandle = attribute.value; }
                    if (attribute.name === 'familyExpansion' || attribute.name === 'includeFamily') { this.includeFamilies = attribute.value === 'true' ? true : false; }
                    if (attribute.name === 'filename') { this.inputFile = attribute.value; }
                    if (attribute.name === 'queryEntered') { this.metadataField = attribute.value; }
                    if (attribute.name === 'filterName') { this.sources = attribute.value.replace(/,/g, ';'); }
                    if (attribute.name === 'providedvalues') { this.providedValues = attribute.value; }
                });
            }
            if (value.reportEntryList && value.reportEntryList.length > 0) {
                value.reportEntryList.forEach(item => {
                    if (item.name.endsWith('Missed Values') || item.name.endsWith('Not Matched')) {
                        this.missedValues = item.count;
                    } else if (item.name.endsWith('Multiple Matched Values') || item.name.endsWith('Multiple Matches')) {
                        this.multipleMatchedValues = item.count;
                    } else {
                        this.matchedValues = item.count;
                    }
                });
            }
        }
    }
}

export class BillingSummaryReport extends Report {

    constructor(value?: Partial<Report>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }
        if (value && value.reportData && value.reportData.length > 0) {
            this.formatedData = [];
            for (let i = 0; i < value.reportData.length; i++) {
                const data = value.reportData[i].split(',');
                if (data.length > 2) {
                    this.formatedData.push({ documentClass: data[0], count: data[1], size: data[2] });
                }
            }
        }
    }
}

export class TechnicalDataReport extends Report {

    constructor(value?: Partial<Report>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }

        this.formatedData = [];
        let containerName = '';
        let msgName, msgAttName, edocName, cDedupEdoc, sDedupEdoc, cDedupMsgFam, sDedupMsgFam, edocOleName, msgOleName, cDedupMsgAtt, sDedupMsgAtt, cDedupEdocOle,
            sDedupEdocOle, cDedupMsgOle, sDedupMsgOle, cTotalDedupDoc, sTotalDedupDoc, DedupEdocName, DedupMsgOleName = '';

        let cContainer = 0,
            sContainer = 0,
            cMsg = 0,
            sMsg = 0,
            cMsgAtt = 0,
            sMsgAtt = 0,
            cTotalDoc = 0,
            sTotalDoc = 0,
            cEdoc = 0,
            sEdoc = 0,
            cEdocOle = 0,
            sEdocOle = 0,
            cMsgOle = 0,
            sMsgOle = 0;

        if (!value?.reportEntryList?.length) {
            return;
        }

        const reportEntryList = value.reportEntryList;
        if(reportEntryList.length > 0) {
        value.reportEntryList.forEach((entry) => {
                switch (entry.name) {
                    case 'Archive':
                    case 'Message_Archive':
                    case 'Disk_Image':
                        containerName += entry.name + ',';
                        cContainer += parseFloat(entry.count);
                        sContainer += parseFloat(entry.size);
                        break;
                    case 'Directory':
                        this.formatedData.push({
                            name: 'Directories',
                            count: parseFloat(entry.count),
                            size: this.formatSizeUnits(entry.size),
                            unformattedSize: entry.size,
                            nonFormattedName: entry.name
                        });
                        break;
                    case 'Message':
                        msgName = entry.name;
                        cMsg = parseFloat(entry.count);
                        sMsg = parseFloat(entry.size);
                        break;
                    case 'Message_Attachment':
                        msgAttName = entry.name;
                        cMsgAtt = parseFloat(entry.count);
                        sMsgAtt = parseFloat(entry.size);
                        break;
                    case 'NIST_EDoc':
                        this.formatedData.push({
                            name: 'NIST EDoc Files',
                            count: parseFloat(entry.count),
                            size: this.formatSizeUnits(entry.size),
                            unformattedSize: entry.size,
                            nonFormattedName: entry.name
                        });
                        break;
                    case 'DeDuped_Message_Attachment':
                        cDedupMsgAtt = entry.count;
                        sDedupMsgAtt = entry.size;
                        break;
                    case 'Non-NIST_EDoc':
                        edocName = entry.name;
                        cEdoc = parseFloat(entry.count);
                        sEdoc = parseFloat(entry.size);
                        break;
                    case 'DeDuped_Non-NIST_EDoc':
                        cDedupEdoc = entry.count;
                        sDedupEdoc = entry.size;
                        DedupEdocName = entry.name;
                        break;
                    case 'DeDuped_Message':
                        cDedupMsgFam = entry.count;
                        sDedupMsgFam = entry.size;
                        break;
                    case 'EDoc_OLE_Attachment':
                        edocOleName = entry.name;
                        cEdocOle = parseFloat(entry.count);
                        sEdocOle = parseFloat(entry.size);
                        break;
                    case 'DeDuped_EDoc_OLE_Attachment':
                        cDedupEdocOle = entry.count;
                        sDedupEdocOle = entry.size;
                        break;
                    case 'Message_OLE_Attachment':
                        msgOleName = entry.name;
                        cMsgOle = parseFloat(entry.count);
                        sMsgOle = parseFloat(entry.size);
                        break;
                    case 'DeDuped_Message_OLE_Attachment':
                        cDedupMsgOle = entry.count;
                        sDedupMsgOle = entry.size;
                        DedupMsgOleName = entry.name;
                        break;
                    case 'Total_Documents':
                        cTotalDoc = (entry.count);
                        sTotalDoc = parseFloat(entry.size);
                        break;
                    case 'DeDuped_Total_Documents':
                        cTotalDedupDoc = entry.count;
                        sTotalDedupDoc = entry.size;
                        break;
                }
            });
        } else {
            this.formatedData.push({
                name: 'NIST EDoc Files',
                count: 0,
                size: 0,
                nonFormattedName: containerName
            });
            this.formatedData.push({
                name: 'Directories',
                count: 0,
                size: 0,
                nonFormattedName: containerName
            });
        }

        if (containerName) {
            containerName = containerName.slice(0, -1);
        }

        this.formatedData.push({
            name: 'Container Files',
            count: cContainer,
            size: this.formatSizeUnits(sContainer),
            unformattedSize: sContainer,
            nonFormattedName: containerName

        });
        this.formatedData.push({
            name: 'Messages',
            count: cMsg,
            size: this.formatSizeUnits(sMsg),
            unformattedSize: sMsg,
            dupCount: cDedupMsgFam,
            dupSize: this.formatSizeUnits(sDedupMsgFam),
            unformattedDupSize: sDedupMsgFam,
            nonFormattedName: msgName
        });
        this.formatedData.push({
            name: 'Message Attachments',
            count: cMsgAtt,
            size: this.formatSizeUnits(sMsgAtt),
            unformattedSize: sMsgAtt,
            dupCount: cDedupMsgAtt,
            dupSize: this.formatSizeUnits(sDedupMsgAtt),
            unformattedDupSize: sDedupMsgAtt,
            nonFormattedName: msgAttName
        });
        this.formatedData.push({
            name: 'Non-NIST EDoc Files',
            count: cEdoc,
            size: this.formatSizeUnits(sEdoc),
            unformattedSize: sEdoc,
            dupCount: cDedupEdoc,
            dupSize: this.formatSizeUnits(sDedupEdoc),
            unformattedDupSize: sDedupEdoc,
            nonFormattedName: edocName
        });
        this.formatedData.push({
            name: 'EDoc OLE Attachments',
            count: cEdocOle,
            size: this.formatSizeUnits(sEdocOle),
            unformattedSize: sEdocOle,
            dupCount: cDedupEdocOle,
            dupSize: this.formatSizeUnits(sDedupEdocOle),
            unformattedDupSize: sDedupEdocOle,
            nonFormattedName: edocOleName
        });
        this.formatedData.push({
            name: 'Message OLE Attachments',
            count: cMsgOle,
            size: this.formatSizeUnits(sMsgOle),
            unformattedSize: sMsgOle,
            dupCount: cDedupMsgOle,
            dupSize: this.formatSizeUnits(sDedupMsgOle),
            unformattedDupSize: sDedupMsgOle,
            nonFormattedName: msgOleName
        });
        this.formatedData.push({
            name: 'Total Documents',
            count: cTotalDoc,
            size: this.formatSizeUnits(sTotalDoc),
            unformattedSize: sTotalDoc,
            dupCount: cTotalDedupDoc,
            dupSize: sTotalDedupDoc === '' || sTotalDedupDoc === undefined ? '' : this.formatSizeUnits(sTotalDedupDoc),
            unformattedDupSize: sTotalDedupDoc === '' || sTotalDedupDoc === undefined ? '' : sTotalDedupDoc,
            isBold: true
        });
    }

    formatSizeUnits(bytes: any): any {
        if (!isNaN(bytes)) {
            if (bytes >= 1073741824) {
                bytes = (bytes / 1073741824).toFixed(1) + ' GB';
            } else if (bytes >= 1048576) {
                bytes = (bytes / 1048576).toFixed(1) + ' MB';
            } else if (bytes >= 1024) {
                bytes = (bytes / 1024).toFixed(1) + ' KB';
            } else if (bytes > 1) {
                bytes = bytes + ' B';
            } else if (bytes === 1) {
                bytes = bytes + ' B';
            } else if (bytes < 1) {
                bytes = bytes + ' B';
            } else {
                bytes = '0 B';
            }
        }
        return bytes;
    }
}

export class OCRSourceReport extends Report {

    constructor(value?: Partial<Report>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }
        if (value && value.reportData && value.reportData.length > 0) {
            const sourceCountData = [];
            value.reportEntryList.forEach(entry => {
                const entryObject = {
                    nonFormattedName: '',
                    name: '',
                    notYet: '0',
                    success: '0',
                    failed: '0',
                    notProcessed: false,
                    failedProcessed: false,
                    successProcessed: false
                };
                if (entry.name.indexOf('Not Processed') > -1) {
                    const parseEntry = entry.name.split(/Not Processed/);
                    entryObject.nonFormattedName = entry.name;
                    entryObject.name = parseEntry[0];
                    entryObject.notYet = entry.count;
                    entryObject.success = '0';
                    entryObject.failed = '0';
                    entryObject.notProcessed = true;
                } else if (entry.name.indexOf('Failed Processing') > -1) {
                    const parseEntry = entry.name.split(/Failed Processing/);
                    entryObject.nonFormattedName = entry.name;
                    entryObject.name = parseEntry[0];
                    entryObject.notYet = '0';
                    entryObject.success = '0';
                    entryObject.failed = entry.count;
                    entryObject.failedProcessed = true;
                } else {
                    const parseEntry = entry.name.split(/Successfully Processed/);
                    entryObject.nonFormattedName = entry.name;
                    entryObject.name = parseEntry[0];
                    entryObject.notYet = '0';
                    entryObject.success = entry.count;
                    entryObject.failed = '0';
                    entryObject.successProcessed = true;
                }
                sourceCountData.push(entryObject);
            });

            const itemsAlreadyAdded = [];
            const entriesDataSet = [];

            sourceCountData.forEach(entry => {
                const itemExists = _.contains(itemsAlreadyAdded, entry.name);
                if (!itemExists) {
                    itemsAlreadyAdded.push(entry.name);
                    const itemGroup = [];
                    const groupSimilarEntries = _.filter(sourceCountData, (item) => item.name === entry.name);
                    itemGroup.push(groupSimilarEntries);
                    entriesDataSet.push(itemGroup);
                }
            });

            const dataSets = [];
            for (let i = 0; i < entriesDataSet.length; i++) {
                const entryDataSet = {
                    nonFormattedName: entriesDataSet[i][0].nonFormattedName, name: entriesDataSet[i][0].name, notYet: '0', failed: '0', success: '0',
                    nonFormattedNameNotProcessed: ''
                };

                let filterEntry = _.filter(entriesDataSet[i][0], (item) => item.notProcessed);

                if (filterEntry.length > 0) {
                    entryDataSet.nonFormattedName = filterEntry[0].nonFormattedName;
                    entryDataSet.nonFormattedNameNotProcessed = filterEntry[0].name + 'Not Processed';
                    entryDataSet.name = filterEntry[0].name;
                    entryDataSet.notYet = filterEntry[0].notYet;
                }

                filterEntry = _.filter(entriesDataSet[i][0], (item) => item.failedProcessed);

                if (filterEntry.length > 0) {
                    entryDataSet.nonFormattedName = filterEntry[0].nonFormattedName;
                    entryDataSet.nonFormattedNameNotProcessed = filterEntry[0].name + 'Not Processed';
                    entryDataSet.name = filterEntry[0].name;
                    entryDataSet.failed = filterEntry[0].failed;
                }

                filterEntry = _.filter(entriesDataSet[i][0], (item) => item.successProcessed);

                if (filterEntry.length > 0) {
                    entryDataSet.nonFormattedName = filterEntry[0].nonFormattedName;
                    entryDataSet.nonFormattedNameNotProcessed = filterEntry[0].name + 'Not Processed';
                    entryDataSet.name = filterEntry[0].name;
                    entryDataSet.success = filterEntry[0].success;
                }

                dataSets.push(entryDataSet);
            }
            this.formatedData = dataSets;
        }
    }
}

export const ReportMap: Map<ReportType, (data: Report, node: GenericTreeNode) => any> = new Map<ReportType, (data: Report, node: GenericTreeNode) => any>([
    [ReportType.SEARCH, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Search Summary',
        reportType: ReportType.SEARCH,
        reportDisplayType: node.data && node.data.task === 'BULK_SEARCH' ? ReportDisplayType.GRID : ReportDisplayType.NONE,
        headerBarVisible: node.data && node.data.task === 'BULK_SEARCH' ? false : true,
        automationId: 'search-summary',
        data: new SearchReport(data),
        node: node,
        isDownloadButtonPresent: true,
        downloadReportExtension: 'xlsx',
        downloadTooltip: '',
        downloadExcelOption: true,
        isDetailsButtonPresent: false,
        isFullWidth: true,
        noBottomMargin: true,
        index: 1,
        visible: node.entityType === EntityType.searchResult || node.entityType === EntityType.savedSearch,
        detailsTitle: 'Search Summary'
    }],
    [ReportType.METADATA_BULK_SEARCH_SUMMARY, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Search Summary',
        reportType: ReportType.METADATA_BULK_SEARCH_SUMMARY,
        reportDisplayType: ReportDisplayType.NONE,
        headerBarVisible: true,
        automationId: 'metadata-bulk-search-summary',
        data: new MetadataBulkSearchReport(data),
        node: node,
        isDownloadButtonPresent: true,
        downloadReportExtension: 'xlsx',
        isDetailsButtonPresent: false,
        downloadTooltip: 'Download Summary and Details (.xlsx)',
        isFullWidth: true,
        index: 1,
        visible: node.entityType === EntityType.searchResult
    }],
    [ReportType.TERM_SEARCH_HIT_COUNT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Results Summary (for Dedupe Setting: Global)',
        reportType: ReportType.TERM_SEARCH_HIT_COUNT,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'search-result-summary-global',
        data: new SearchResultReport(data),
        node: node,
        isDownloadButtonPresent: false,
        isDetailsButtonPresent: false,
        isFullWidth: true,
        index: 2,
        visible: node.entityType === EntityType.searchResult || node.entityType === EntityType.savedSearch,
        gridContext: new GridContext({
            sortBy: 'id',
            sortAscending: true,
            sortEnabled: true
        }),
    }],
    [ReportType.TERM_SEARCH_HIT_COUNT_VERTICAL, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Results Summary (for Dedupe Setting: Custodial)',
        reportType: ReportType.TERM_SEARCH_HIT_COUNT_VERTICAL,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'search-result-summary-custodial',
        data: new SearchResultReport(data),
        node: node,
        isDownloadButtonPresent: false,
        isDetailsButtonPresent: false,
        isFullWidth: true,
        index: 2,
        visible: node.entityType === EntityType.searchResult || node.entityType === EntityType.savedSearch,
        gridContext: new GridContext({
            sortBy: 'id',
            sortAscending: true,
            sortEnabled: true
        }),
    }],
    [ReportType.WORKFLOW, (data: Report) => <any>{
        name: '',
        reportType: ReportType.WORKFLOW,
        // automationId: 'workflow-report',
        data: new WorkflowReport(data),
        isDownloadButtonPresent: false,
        isDetailsButtonPresent: false,
        isFullWidth: true,
        index: 0,
        visible: true,
        gridContext: new GridContext({
            sortBy: 'id',
            sortAscending: true,
            sortEnabled: true
        }),
    }],

    [ReportType.DOCUMENT_CLASS, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Document Classes',
        reportType: ReportType.DOCUMENT_CLASS,
        reportDisplayType: ReportDisplayType.PIE_CHART,
        chartBySize: true,
        headerBarVisible: true,
        headerBarTabs: true,
        automationId: 'document-classes',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 5 }, { type: EntityType.import, index: 9 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Download Document Classes (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Document Class',
        isBusy: false
    }],
    [ReportType.FILETYPE, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Document Types',
        reportType: ReportType.FILETYPE,
        reportDisplayType: ReportDisplayType.PIE_CHART,
        chartBySize: true,
        headerBarVisible: true,
        headerBarTabs: true,
        automationId: 'document-types',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: 'Dashboard', index: 1 }, { type: EntityType.projectData, index: 6 }, { type: EntityType.import, index: 10 }],
        visible: true,
        detailsType: 'EXCEPTION_DETAILS',
        downloadTooltip: 'Download Document Types (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Document Type',
        isBusy: false
    }],
    [ReportType.TECHNICAL_REDUCTION, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Document Classification (for Dedupe Setting: Global)',
        reportType: ReportType.TECHNICAL_REDUCTION,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'technical-reduction-global',
        data: data ? new TechnicalDataReport(data) : new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: 'Dashboard', index: 2 }, { type: EntityType.projectData, index: 3 }, { type: EntityType.import, index: 7 }],
        visible: true,
        downloadTooltip: 'Document Classification (for Dedupe Setting: Global) (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Document Classification (for Dedupe Setting: Global)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.TECHNICAL_REDUCTION_VERTICAL, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Document Classification (for Dedupe Setting: Custodial)',
        reportType: ReportType.TECHNICAL_REDUCTION_VERTICAL,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'technical-reduction-custodial',
        data: data ? new TechnicalDataReport(data) : new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: 'Dashboard', index: 2 }, { type: EntityType.projectData, index: 3 }, { type: EntityType.import, index: 7 }],
        visible: true,
        downloadTooltip: 'Document Classification (for Dedupe Setting: Custodial) (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Document Classification (for Dedupe Setting: Custodial)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.GROUP_VIEW_MEMBERSHIP, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Custodians',
        reportType: ReportType.GROUP_VIEW_MEMBERSHIP,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: true,
        headerBarVisible: true,
        headerBarTabs: true,
        automationId: 'document-by-custodians',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 8 }],
        visible: !((node.data && (node.data.viewType === ViewType.CORPUS_SET_RESULT ||
            node.data.viewType === ViewType.CORPUS_RESULT))) && node.entityType !== EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Custodians (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Custodian',
        isBusy: false
    }],
    [ReportType.CUSTODIAN_DIRECTORY_REPORT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Custodian Directories',
        reportType: ReportType.CUSTODIAN_DIRECTORY_REPORT,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: true,
        headerBarVisible: true,
        headerBarTabs: true,
        automationId: 'document-by-custodian-directories',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 9 }, { type: EntityType.import, index: 11 }],
        visible: (node.entityType === EntityType.searchResult &&
            (node.data && node.data.viewType === ViewType.CORPUS_SET_RESULT ||
                node.data.viewType === ViewType.CORPUS_RESULT || (node.data.name && node.data.name.indexOf('Drill through') > -1))) || node.entityType === EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Custodian Directories (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Custodian Directory',
        isBusy: false
    }],
    [ReportType.CORPUS_SOURCE, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Sources',
        reportType: ReportType.CORPUS_SOURCE,
        reportDisplayType: ReportDisplayType.PIE_CHART,
        chartBySize: true,
        headerBarVisible: true,
        headerBarTabs: true,
        automationId: 'document-by-source',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 10 }, { type: EntityType.import, index: 12 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Sources (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Document Source',
        isBusy: false
    }],
    [ReportType.TAG_SUMMARY, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Tags',
        reportType: ReportType.TAG_SUMMARY,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: true,
        headerBarVisible: true,
        headerBarTabs: true,
        automationId: 'document-count-by-tags',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 11,
        visible: !((node.data && (node.data.viewType === ViewType.CORPUS_SET_RESULT ||
            node.data.viewType === ViewType.CORPUS_RESULT))) && node.entityType !== EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Tags (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Tag',
        isBusy: false
    }],
    [ReportType.EMAIL_SENT_DOMAINS, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Sending Domains',
        reportType: ReportType.EMAIL_SENT_DOMAINS,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'sending-domains',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 12 }, { type: EntityType.import, index: 13 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Sending Domains (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Sending Domains',
        isBusy: false
    }],
    [ReportType.EMAIL_RECEIVED_DOMAINS, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Receiving Domains',
        reportType: ReportType.EMAIL_RECEIVED_DOMAINS,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'receiving-domains',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 13 }, { type: EntityType.import, index: 14 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Receiving Domains (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Receiving Domains',
        isBusy: false
    }],
    [ReportType.DOMINANT_LANGUAGE, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Dominant Languages',
        reportType: ReportType.DOMINANT_LANGUAGE,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'dominant-languages',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 14 }, { type: EntityType.import, index: 15 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Dominant Languages (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Dominant Language',
        isBusy: false
    }],
    [ReportType.LANGUAGE, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Languages',
        reportType: ReportType.LANGUAGE,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'languages',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 15 }, { type: EntityType.import, index: 16 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Languages (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Language',
        isBusy: false
    }],
    [ReportType.DATE_EMAIL_SENT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Email Sent Date',
        reportType: ReportType.DATE_EMAIL_SENT,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'email-sent-date',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isZeroDayDetailsButtonPresent: true,
        isFullWidth: true,
        index: 0,
        indexLookUp: [{ type: 'Dashboard', index: 3 }, { type: EntityType.projectData, index: 16 }, { type: EntityType.import, index: 17 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Document Email Sent Date (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Email Sent Date',
        isBusy: false
    }],
    [ReportType.DATE_EMAIL_RECEIVED, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Email Received Date',
        reportType: ReportType.DATE_EMAIL_RECEIVED,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'email-received-date',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isZeroDayDetailsButtonPresent: true,
        isFullWidth: true,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 17 }, { type: EntityType.import, index: 18 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Document Email Received Date (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Email Received Date',
        isBusy: false
    }],
    [ReportType.DATE_PRIMARY, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Date Report',
        reportType: ReportType.DATE_PRIMARY,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'date',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isZeroDayDetailsButtonPresent: true,
        isFullWidth: true,
        index: 18,
        visible: !((node.data && (node.data.viewType === ViewType.CORPUS_SET_RESULT ||
            node.data.viewType === ViewType.CORPUS_RESULT))) && node.entityType !== EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Document Date Report (.csv)',
        downloadReportExtension: 'csv',
        detailsTitle: 'Date Report',
        isBusy: false
    }],
    [ReportType.EMAIL_SENDER, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Email Addresses Sent',
        reportType: ReportType.EMAIL_SENDER,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'email-addresses-sent',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 19,
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Email Addresses Sent (.xlsx)',
        downloadReportExtension: 'xlsx',
        detailsTitle: 'Email Addresses Sent',
        isBusy: false
    }],
    [ReportType.EMAIL_RECEIVER, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Email Addresses Received',
        reportType: ReportType.EMAIL_RECEIVER,
        reportDisplayType: ReportDisplayType.BAR_CHART,
        chartBySize: false,
        headerBarVisible: true,
        automationId: 'email-addresses-received',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 20,
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Email Addresses Received (.xlsx)',
        downloadReportExtension: 'xlsx',
        detailsTitle: 'Email Addresses Received',
        isBusy: false
    }],
    [ReportType.BILLING_REPORT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Billing Summary',
        reportType: ReportType.BILLING_REPORT,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'billing-summary',
        data: new BillingSummaryReport(data),
        node: node,
        isDetailsButtonPresent: true,
        isDownloadButtonPresent: true,
        downloadReportExtension: 'xlsx',
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 4 }, { type: EntityType.import, index: 8 }],
        visible: node.entityType === EntityType.projectData || node.entityType === EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Document Billing Summary (.xlsx)',
        detailsTitle: 'Billing Summary',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.CUSTOM_WARNINGS_ERRORS, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Custom Warnings and Errors',
        reportType: ReportType.CUSTOM_WARNINGS_ERRORS,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'custom-warnings-and-errors',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 3,
        visible: node.entityType === EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Custom Warnings and Errors (.csv)',
        detailsTitle: 'Custom Warnings and Errors',
        gridContext: new GridContext({
            sortBy: SortByFilter.COUNT.toLowerCase(),
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.IMPORT_ERROR, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Warnings and Errors',
        reportType: ReportType.IMPORT_ERROR,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'warnings-and-errors',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        downloadReportExtension: 'xlsx',
        isFullWidth: false,
        index: 2,
        visible: node.entityType === EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Warnings and Errors (.xlsx)',
        detailsTitle: 'Warnings and Errors',
        gridContext: new GridContext({
            sortBy: SortByFilter.COUNT.toLowerCase(),
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.IMPORT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Scan Summary',
        reportType: ReportType.IMPORT,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'scan-summary',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 1,
        visible: node.entityType === EntityType.import && node.data instanceof Corpus,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Scan Summary (.csv)',
        detailsTitle: 'Scan',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: false
        }),
        isBusy: false
    }],
    [ReportType.OCR_CONFIDENCE_LEVEL, (data: Report, node: GenericTreeNode) => <any>{
        name: 'OCR Confidence',
        reportType: ReportType.OCR_CONFIDENCE_LEVEL,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'ocr-confidence',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 0,
        indexLookUp: [{ type: EntityType.projectData, index: 7 }, { type: EntityType.import, index: 5 }],
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'OCR Confidence (.csv)',
        gridContext: new GridContext({
            sortBy: SortByFilter.COUNT.toLowerCase(),
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.OCR_CANDIDATES, (data: Report, node: GenericTreeNode) => <any>{
        name: 'OCR Candidates',
        reportType: ReportType.OCR_CANDIDATES,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'ocr-candidates',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 4,
        visible: node.entityType === EntityType.import,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'OCR Candidates (.csv)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: true
        }),
        isBusy: false
    }],
    [ReportType.OCR_SOURCE, (data: Report, node: GenericTreeNode) => <any>{
        name: 'OCR Sources',
        reportType: ReportType.OCR_SOURCE,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'ocr-sources',
        data: data ? new OCRSourceReport(data) : new Report(data),
        node: node,
        isDownloadButtonPresent: false,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 6,
        visible: node.entityType === EntityType.import && !(node.data instanceof Corpus),
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'OCR Sources (.csv)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: true,
            sortBy: 'notYet',
        }),
        isBusy: false
    }],
    [ReportType.EXPORT_COMPARE, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Search Deduped against Export Details',
        reportType: ReportType.EXPORT_COMPARE,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'search-deduped-against-export-details',
        data: data ? new ExportComparisonReport(data) : new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'xlsx',
        isFullWidth: false,
        index: -1,
        visible: node && node.data && node.data.exportComparison,
        downloadTooltip: 'Search Deduped against Export Details (.xlsx)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: false
        })
    }],
    [ReportType.EXPORT_COMPARE_STATISTICS, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Export Statistics',
        reportType: ReportType.EXPORT_COMPARE_STATISTICS,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'export-statistics',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: false,
        isDetailsButtonPresent: false,
        isFullWidth: false,
        index: 0,
        visible: node && node.data && node.data.exportComparison,
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: true
        })
    }],
    [ReportType.EXPORT_LOAD_FILE_REPORT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Load File Report',
        reportType: ReportType.EXPORT_LOAD_FILE_REPORT,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'export-load-file-report',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 1,
        visible: node && node.entityType === EntityType.exportVolume,
        downloadTooltip: 'Load File Report (.csv)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: false
        })
    }],
    [ReportType.EXPORT_PREPARE_REPORT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Pre-Production Report',
        reportType: ReportType.EXPORT_PREPARE_REPORT,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'export-pre-production',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 2,
        visible: node && node.entityType === EntityType.exportVolume,
        downloadTooltip: 'Pre-Production Report (.csv)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: false
        })
    }],
    [ReportType.EXPORT_PRODUCE_REPORT, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Production Report',
        reportType: ReportType.EXPORT_PRODUCE_REPORT,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'export-production',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: false,
        downloadReportExtension: 'csv',
        isFullWidth: false,
        index: 3,
        visible: node && node.entityType === EntityType.exportVolume && node.data.exportVolumeDisplayState !== 'STAGED',
        downloadTooltip: 'Production Report (.csv)',
        gridContext: new GridContext({
            sortAscending: false,
            sortEnabled: false
        })
    }],
    [ReportType.REVIEW_FOLDER, (data: Report, node: GenericTreeNode) => <any>{
        name: 'Folder Path Report',
        reportType: ReportType.REVIEW_FOLDER,
        reportDisplayType: ReportDisplayType.GRID,
        headerBarVisible: true,
        automationId: 'folder-path',
        data: new Report(data),
        node: node,
        isDownloadButtonPresent: true,
        isDetailsButtonPresent: true,
        isFullWidth: false,
        index: 21,
        visible: true,
        detailsType: 'STANDARD_DETAILS',
        downloadTooltip: 'Folder Path Report (.xlsx)',
        downloadReportExtension: 'xlsx',
        detailsTitle: 'Folder Path',
        gridContext: new GridContext({
            sortBy: SortByFilter.COUNT.toLowerCase(),
            sortAscending: false,
            sortEnabled: false
        }),
        isBusy: false
    }],
]);

